import { createModel } from "@rematch/core";

import type { RootModel } from ".";
import { CustomerTypes } from "../types/customer.types";
import { SubscriptionTypes } from "../types/subscription.types";

import { LatLng } from "../components/event-map";

/**
 * Store for the CURRENT USER's customer context.
 * For admin management of all customers, see `customersAdmin` store.
 */
export interface CustomerStoreProps {
    loading: {
        finishOnboarding: boolean,
        getDetails: boolean,
        changeInviteCode: boolean,
        inviteUserToSubscription: boolean,
        removeUserFromSubscription: boolean,
        changeLicenseCount: boolean,
    },
    selectedSubscription: SubscriptionTypes.Model,
    details: {
        customer: CustomerTypes.Model,
        subscriptions: SubscriptionTypes.Model[],
    },
    minAppVersion: string | null,
}

export const customerInitialState: CustomerStoreProps = {
    loading: {
        finishOnboarding: false,
        getDetails: false,
        changeInviteCode: false,
        inviteUserToSubscription: false,
        removeUserFromSubscription: false,
        changeLicenseCount: false,
    },
    selectedSubscription: {} as SubscriptionTypes.Model,
    details: {
        customer: {} as CustomerTypes.Model,
        subscriptions: [] as SubscriptionTypes.Model[],
    },
    minAppVersion: null as string | null,
};

export const customer = createModel<RootModel>()({
    state: customerInitialState,
    reducers: {
        boot: (store) => {
            store = customerInitialState;
        },
        setSelectedSubscription: (store, sub: SubscriptionTypes.Model) => {
            store.selectedSubscription = sub;
        },
        onChangeInviteCode: (store, params: { inviteCode: string, subscriptionId: string }) => {
            if (store.selectedSubscription.id === params.subscriptionId) {
                store.selectedSubscription.inviteCode = params.inviteCode;
            }
            store.details.subscriptions = store.details.subscriptions.map((sub) => {
                if (sub.id === params.subscriptionId) {
                    return { ...sub, inviteCode: params.inviteCode };
                }

                return sub;
            });
        },
        // Mirror of onChangeInviteCode for the POM Master license-count edit
        // (ClickUp 868m4vzj7) so the customer Settings page card updates in place.
        onChangeLicenseCount: (store, params: { licenseCount: number, subscriptionId: string }) => {
            if (store.selectedSubscription.id === params.subscriptionId) {
                store.selectedSubscription.licenseCount = params.licenseCount;
            }
            store.details.subscriptions = store.details.subscriptions.map((sub) => {
                if (sub.id === params.subscriptionId) {
                    return { ...sub, licenseCount: params.licenseCount };
                }

                return sub;
            });
        },
        setDetails: (store, details: CustomerStoreProps['details']) => {
            store.details = details;
        },
        toggleLoading: (
            store, prop: keyof CustomerStoreProps['loading'], v: boolean) => {
            store.loading[prop] = v;
        },
        // The per-flag gate setters (setShowInsightsDashboard, …) that used to
        // live here are gone: visibility gates read useEffectiveSettings
        // (GET /v2/settings/me) instead of this redux slice. alertSoundUrl is
        // the one settings key still consumed from here (play-sound-button).
        setAlertSoundUrl: (store, alertSoundUrl: string) => {
            store.details.customer.settings = { ...store.details.customer.settings, alertSoundUrl }
        },
        setLocalPosition: (store, position: LatLng ) => {
            store.details.customer.localPosition = position;
        },
        setHomePosition: (store, position: LatLng) => {
            store.details.customer.homePosition = position;
        },
        setWorkPosition: (store, position: LatLng) => {
            store.details.customer.workPosition = position;
        },
        setMinAppVersion: (store, minAppVersion: string) => {
            store.minAppVersion = minAppVersion;
        },
    },
});